'use client';

import { useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  AboutSection,
  BundlesSection,
  CommutesSection,
  GapSection,
  HeroSection,
  HomeProsFooter,
  HomeProsHeader,
  PricingSection,
  ProofSection,
  ReviewsSection,
  ServicesSection,
  StickyQuoteButton,
  GuidedFormSection,
} from './components/sections';
import type { HeroCopy } from './components/sections/Hero';
import type {
  Bundle,
  ConsultService,
  GalleryItem,
  NavLink,
  PainPoint,
  Palette,
  PriceStep,
  ReviewItem,
  ServiceDetail,
} from './components/sections/types';

const WP_MARKETING_ENDPOINT =
  process.env.NEXT_PUBLIC_HOME_PROS_WP_ENDPOINT || 'https://homepros4tx.com/wp-json/hp/v1/marketing';
const WP_MENU_ENDPOINT =
  process.env.NEXT_PUBLIC_HOME_PROS_WP_MENU ||
  'https://homepros4tx.com/wp-json/homepros/v1/menu?location=primary&tree=1&depth=3';

type MenuNode = {
  id: number;
  title?: string | null;
  href?: string | null;
  target?: string;
  classes?: string[];
  children?: MenuNode[];
};

type HomeProsMarketing = Partial<{
  palette: Partial<Palette>;
  hero: HeroCopy;
  navLinks: NavLink[];
  serviceNav: NavLink[];
  painPoints: PainPoint[];
  serviceDetails: ServiceDetail[];
  priceSteps: PriceStep[];
  galleryItems: GalleryItem[];
  reviews: ReviewItem[];
  bundles: Bundle[];
  consultServices: ConsultService[];
}>;

const defaultPalette: Palette = {
  sage: '#8FA895',
  forest: '#2C4A3B',
  cream: '#F9F9F7',
  white: '#FFFFFF',
  charcoal: '#1F2937',
  clay: '#D97706',
};

const defaultHero: HeroCopy = {
  badge: 'Your Vision, Our Expertise',
  title: 'Make reality match',
  highlight: 'your vision.',
  blurb:
    'Bridging the gap between the house you have and the home you want. We coordinate roofing, water, and lighting upgrades so the results feel intentional and the price makes sense.',
  primaryCtaText: 'Get a Quote',
  primaryCtaHref: '#quote',
  secondaryCtaText: 'Services',
  secondaryCtaHref: '#services',
  trustPill: 'Trusted coordination.',
};

const HOME_PROS_DOMAIN = 'https://homepros4tx.com';

const defaultNavLinks: NavLink[] = [
  { label: 'Services', href: '#services' },
  { label: 'Pricing Promise', href: '#pricing' },
  { label: 'Gallery', href: '#proof' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'About', href: '#about' },
  { label: 'Blogs', href: '/blogs' },
];

const defaultServiceNav: NavLink[] = [
  { label: 'Roofing', href: '/home-pros/roofing' },
  { label: 'Water Softeners', href: '/home-pros/water' },
  { label: 'Permanent Lighting', href: '/home-pros/lighting' },
  { label: 'Back Up Power (Generators)', href: '/home-pros/generators' },
];

const defaultPainPoints: PainPoint[] = [
  { title: 'Streaked or aging roof', note: 'Heat, hail, and storms make “good enough” roofs fail faster.' },
  { title: 'Hard water buildup & dry skin', note: 'Scale wrecks fixtures and keeps your home from feeling premium.' },
  { title: 'Temporary holiday lights every year', note: 'Ladders, tangled cords, and no year-round curb appeal.' },
  { title: 'High power bills', note: 'Electrical upgrades make energy usage safer and more efficient.' },
];

const defaultServiceDetails: ServiceDetail[] = [
  {
    title: 'Roofing',
    benefit: 'Impact-rated systems built for heat, storms, and long-term performance.',
    bullets: ['Heat & hail tested assemblies', 'Insurance-smart scopes'],
    slug: 'roofing',
    accent: defaultPalette.forest,
  },
  {
    title: 'Water Softeners',
    benefit: 'Soft water, clean fixtures, better skin & laundry.',
    bullets: ['Whole-home softening + filtration', 'Sized to your usage'],
    slug: 'water',
    accent: defaultPalette.sage,
  },
  {
    title: 'Permanent Lighting',
    benefit: 'Celebrations on-demand, discreet by day.',
    bullets: ['Architectural tracks', 'App presets & install included'],
    slug: 'lighting',
    accent: '#f6d7ba', // warm beige
  },
  {
    title: 'Back Up Power (Generators)',
    benefit: 'Generator planning and backup power setup coordinated with your project.',
    bullets: ['Generator sizing and placement', 'Transfer switch and safety planning'],
    slug: 'generators',
    accent: '#f1c27d', // sunny accent
  },
];

const defaultPriceSteps: PriceStep[] = [
  { title: 'Upload your quote', copy: 'Snap a photo or PDF of the written scope you received.' },
  { title: 'We verify details', copy: 'Materials, warranty, and labor lined up side-by-side.' },
  { title: 'We match & lock', copy: 'Lock pricing, pick an install window, keep the same quality.' },
];

const defaultGalleryItems: GalleryItem[] = [
  { src: '/home-pros/solarpanels/solarpanels.webp', alt: 'Modern home exterior', label: 'Impact-Rated Roofing' },
  { src: '/home-pros/solarpanels/solarpanels2.webp', alt: 'Exterior lighting installed', label: 'Permanent Lighting' },
  { src: '/home-pros/solarpanels/solarpanels3.webp', alt: 'Backup power generator project', label: 'Back Up Power (Generators)' },
  { src: '/home-pros/solarpanels/solarpanels4.webp', alt: 'Clean fascia and panels', label: 'Precision Install' },
];

const defaultReviews: ReviewItem[] = [
  {
    quote: 'Roof + lights done in one week. Crew cleaned daily and sent photos.',
    name: 'Elena & Marco',
    tag: 'Roofing + Lighting',
  },
  {
    quote: 'Water finally feels soft and the fixtures aren’t staining. Financing was simple.',
    name: 'Priya K.',
    tag: 'Water Softeners',
  },
  {
    quote: 'They explained backup power generator options after the core scope was clear.',
    name: 'Derrick H.',
    tag: 'Back Up Power (Generators)',
  },
];

const defaultBundles: Bundle[] = [
  {
    title: 'Roof + Lighting',
    copy: 'Pair roof replacement with discreet lighting channels so fascia work only happens once.',
  },
  {
    title: 'Roof + Water',
    copy: 'Protect the shell and the inside—heat-reducing roof plus soft water for fixtures.',
  },
  {
    title: 'Water + Lighting',
    copy: 'Make the inside feel premium and the outside party-ready without seasonal installs.',
  },
  {
    title: 'Back Up Power (Generators)',
    copy: 'After the core scope is locked, we plan generator-ready backup power options for the full project.',
  },
];

const defaultConsultServices: ConsultService[] = [
  { key: 'roofing', label: 'Roofing' },
  { key: 'water', label: 'Water Softening' },
  { key: 'lighting', label: 'Permanent Lighting' },
  { key: 'generators', label: 'Back Up Power' },
];

const isSolarLabel = (label?: string) => /solar/i.test(label || '');
const isSolarService = (key?: string, label?: string, slug?: string) =>
  key === 'solar' || slug === 'solar' || isSolarLabel(label);

export function HomeProsPageClient() {
  const pathname = usePathname();
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [scrollY, setScrollY] = useState(0);
  const [cms, setCms] = useState<HomeProsMarketing | null>(null);
  const [menuTree, setMenuTree] = useState<MenuNode[] | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let embedded = false;
    try {
      embedded = window.parent !== window;
    } catch {
      embedded = true;
    }
    if (!embedded) return;

    const toTopHashUrl = (href: string): string => {
      try {
        const parsed = new URL(href, window.location.href);
        if (!parsed.hash) return '';
        if (parsed.origin !== window.location.origin) return '';
        return parsed.pathname + parsed.search + parsed.hash;
      } catch {
        return '';
      }
    };

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest('a[href]');
      if (!(link instanceof HTMLAnchorElement)) return;

      const rawHref = link.getAttribute('href') || '';
      if (!rawHref || !rawHref.includes('#')) return;

      const nextUrl = toTopHashUrl(rawHref);
      if (!nextUrl) return;

      event.preventDefault();
      try {
        if (embedded) window.open(nextUrl, '_top');
        else window.location.assign(nextUrl);
      } catch {
        window.location.assign(nextUrl);
      }
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useEffect(() => {
    const scrollToCurrentHash = () => {
      const hash = window.location.hash;
      if (!hash) return;
      const id = decodeURIComponent(hash.slice(1));
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const timer = window.setTimeout(scrollToCurrentHash, 250);
    window.addEventListener('hashchange', scrollToCurrentHash);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('hashchange', scrollToCurrentHash);
    };
  }, []);

  // Fetch WP marketing copy once; silent failure falls back to defaults.
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MARKETING_ENDPOINT, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
          signal: ctrl.signal,
        });
        if (!r.ok) return;
        const data = (await r.json()) as HomeProsMarketing;
        if (!cancelled) setCms(data || null);
      } catch {
        /* default copy will be used */
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MENU_ENDPOINT, {
          method: 'GET',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          signal: ctrl.signal,
          cache: 'no-store',
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as MenuNode[];
        if (!cancelled && Array.isArray(data) && data.length) {
          setMenuTree(data);
        }
      } catch {
        if (!cancelled) setMenuTree(null);
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  const cleanHero = (hero?: HeroCopy | null): HeroCopy | undefined => {
    if (!hero) return undefined;
    const entries = Object.entries(hero).filter(([, v]) => typeof v === 'string' ? v.trim() !== '' : Boolean(v));
    return Object.fromEntries(entries) as HeroCopy;
  };

  const palette = useMemo(
    () => ({ ...defaultPalette, ...(cms?.palette || {}) }),
    [cms?.palette]
  );

  const heroCopy = useMemo(
    () => ({ ...defaultHero, ...(cleanHero(cms?.hero) || {}) }),
    [cms?.hero]
  );

  const stripTags = (value: string) => value.replace(/<[^>]+>/g, '').trim();
  const safeTrim = (value?: string | null) => (typeof value === 'string' ? value.trim() : '');
  const isLandingPath = pathname === '/' || pathname === '/home-pros' || pathname === '/home-pros/';
  const toAbsoluteHref = (href: string) => {
    if (!href) return href;
    if (href.startsWith('http://') || href.startsWith('https://')) return href;
    if (href.startsWith('#')) return isLandingPath ? href : `${HOME_PROS_DOMAIN}/${href}`;
    // Keep Home Pros app routes internal so Next.js client navigation works everywhere, including mobile header links.
    if (href === '/home-pros' || href.startsWith('/home-pros/')) return href;
    if (href.startsWith('/')) return `${HOME_PROS_DOMAIN}${href}`;
    return href;
  };

const inferServiceHref = (label: string) => {
  const normalized = label.toLowerCase();
  if (normalized.includes('roof')) return '/home-pros/roofing';
  if (normalized.includes('water') || normalized.includes('soft')) return '/home-pros/water';
  if (normalized.includes('light')) return '/home-pros/lighting';
  if (
    normalized.includes('generator') ||
    normalized.includes('backup') ||
    normalized.includes('back up') ||
    normalized.includes('power')
  ) {
    return '/home-pros/generators';
  }
  if (
    normalized.includes('solar')
  ) {
    return '/home-pros/solar';
  }
  return '';
};

  const normalizeServiceHref = (href: string, label: string) => {
    const raw = safeTrim(href);
    if (!raw || raw === '#') return inferServiceHref(label);
    if (/^\/home-pros\/solar\/?$/i.test(raw) && /generator|backup|back up|power/i.test(label)) {
      return '/home-pros/generators';
    }
    if (raw === '/home-pros' || raw.startsWith('/home-pros/')) return raw;

    const inferredFromHref = inferServiceHref(raw);
    if (inferredFromHref) return inferredFromHref;

    const inferredFromLabel = inferServiceHref(label);
    if (inferredFromLabel) return inferredFromLabel;

    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith('/')) return `${HOME_PROS_DOMAIN}${raw}`;
    return raw;
  };

  const toNavLink = (node: MenuNode): NavLink => ({
    label: stripTags(node.title || '') || safeTrim(node.href) || 'Link',
    href: safeTrim(node.href),
  });

  const wpNavLinks = useMemo(() => {
    if (!menuTree?.length) return null;
    const isCta = (node: MenuNode) =>
      (node.classes || []).some((cls) =>
        ['cta', 'cta-button', 'button', 'btn', 'primary', 'highlight'].includes(cls),
      );
    return menuTree.filter((node) => !isCta(node)).map(toNavLink);
  }, [menuTree]);

  const wpServiceNav = useMemo(() => {
    if (!menuTree?.length) return null;
    const servicesNode = menuTree.find((node) => {
      const label = stripTags(node.title || '').toLowerCase();
      return label.includes('services') || node.href === '#services';
    });
    if (!servicesNode?.children?.length) return null;
    return servicesNode.children.map(toNavLink);
  }, [menuTree]);

  const sanitizePrimaryNav = (items: NavLink[]): NavLink[] =>
    items
      .map((item) => {
        const href = safeTrim(item.href);
        if (!href || href === '#') return null;
        const label = stripTags(item.label || '') || href;
        if (!label) return null;
        return { label, href: toAbsoluteHref(href) };
      })
      .filter((item): item is NavLink => Boolean(item));

  const sanitizeServiceNav = (items: NavLink[]): NavLink[] =>
    items
      .map((item) => {
        const rawLabel = stripTags(item.label || '');
        if (!rawLabel) return null;
        const rawHref = safeTrim(item.href);
        const href = normalizeServiceHref(rawHref, rawLabel);
        if (!href) return null;
        return {
          label: isSolarLabel(rawLabel) ? 'Back Up Power (Generators)' : rawLabel,
          href,
        };
      })
      .filter((item): item is NavLink => Boolean(item));

  const fallbackNavLinks = sanitizePrimaryNav(defaultNavLinks);
  const resolvedWpNavLinks = wpNavLinks?.length ? sanitizePrimaryNav(wpNavLinks) : [];
  const navLinks = resolvedWpNavLinks.length ? resolvedWpNavLinks : fallbackNavLinks;

  const fallbackServiceNav = sanitizeServiceNav(defaultServiceNav);
  const resolvedWpServiceNav = wpServiceNav?.length ? sanitizeServiceNav(wpServiceNav) : [];
  const serviceNav = resolvedWpServiceNav.length ? resolvedWpServiceNav : fallbackServiceNav;
  const painPoints = cms?.painPoints && cms.painPoints.length ? cms.painPoints : defaultPainPoints;
  const serviceDetails = (
    cms?.serviceDetails && cms.serviceDetails.length ? cms.serviceDetails : defaultServiceDetails
  ).map((detail) =>
    isSolarService(undefined, detail.title, detail.slug)
      ? {
          ...detail,
          slug: 'generators',
          title: 'Back Up Power (Generators)',
          benefit:
            'Generator planning and backup power setup coordinated with your project.',
          bullets: ['Generator sizing and placement', 'Transfer switch and safety planning'],
        }
      : detail
  );
  const priceSteps = cms?.priceSteps && cms.priceSteps.length ? cms.priceSteps : defaultPriceSteps;
  const galleryItems =
    cms?.galleryItems && cms.galleryItems.length ? cms.galleryItems : defaultGalleryItems;
  const reviews = cms?.reviews && cms.reviews.length ? cms.reviews : defaultReviews;
  const bundles = (cms?.bundles && cms.bundles.length ? cms.bundles : defaultBundles).map((bundle) => {
    const title = isSolarLabel(bundle.title) ? 'Back Up Power (Generators)' : bundle.title;
    const copy = (bundle.copy || '')
      .replace(/solar\s*\(add-?on\)/gi, 'Back Up Power (Generators)')
      .replace(/solar add-?ons?/gi, 'Back Up Power (Generators)')
      .replace(/\bsolar\b/gi, 'Back Up Power');
    return { ...bundle, title, copy };
  });
  const consultServices = (
    cms?.consultServices && cms.consultServices.length ? cms.consultServices : defaultConsultServices
  ).map((svc) => ({
    ...svc,
    label: isSolarService(svc.key, svc.label) ? 'Back Up Power' : svc.label,
  }));

  const handlePrimarySelect = (key: string) => {
    setSelectedServices([key]);
  };

  const handleServiceToggle = (key: string) => {
    setSelectedServices((prev) => {
      if (prev.includes(key)) return prev.filter((item) => item !== key);
      return [...prev, key];
    });
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden font-sans selection:bg-emerald-200 selection:text-emerald-900">
      
      {/* --- 1. GLOBAL ATMOSPHERE LAYER (Fixed) --- */}
      {/* This creates the "One Page" feel by persisting behind all light sections */}
      <div className="fixed inset-0 -z-50 overflow-hidden bg-[#F9F9F7]">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='1'/%3E%3C/svg%3E")`,
          }}
        />
        
        {/* Moving Orb 1 (Sage) - Top Left */}
        <div 
          className="absolute top-[-10%] left-[-10%] h-[80vw] w-[80vw] rounded-full mix-blend-multiply blur-[120px] opacity-40 transition-transform duration-1000 ease-out will-change-transform"
          style={{ 
            background: palette.sage,
            transform: `translateY(${scrollY * 0.1}px)` // Subtle Parallax
          }} 
        />
        
        {/* Moving Orb 2 (Clay/Amber) - Bottom Right */}
        <div 
          className="absolute bottom-[-10%] right-[-10%] h-[80vw] w-[80vw] rounded-full mix-blend-multiply blur-[120px] opacity-30 transition-transform duration-1000 ease-out will-change-transform"
          style={{ 
            background: palette.clay,
            transform: `translateY(${-scrollY * 0.1}px)` // Reverse Parallax
          }} 
        />
      </div>

      {/* --- 2. HEADER (Sticky & Glass) --- */}
      <HomeProsHeader palette={palette} navLinks={navLinks} serviceNav={serviceNav} />

      {/* --- 3. SECTIONS --- */}
      
      {/* Hero (Dark Theme - Breaks the flow deliberately for impact) */}
      <div className="relative z-10 shadow-2xl">
        <HeroSection palette={palette} copy={heroCopy} />
      </div>

      {/* Light Sections Group 1 */}
      <div className="relative z-10">
        <GapSection palette={palette} painPoints={painPoints} />
        <AboutSection palette={palette} />
        <ServicesSection palette={palette} serviceDetails={serviceDetails} />
      </div>

      {/* Pricing (Dark Theme - Visual Reset / Breaker) */}
      <div className="relative z-20 my-10 shadow-2xl">
         <PricingSection palette={palette} priceSteps={priceSteps} />
      </div>

      {/* Light Sections Group 2 */}
      <div className="relative z-10">
        <ProofSection palette={palette} galleryItems={galleryItems} />
        <ReviewsSection palette={palette} reviews={reviews} />
        <BundlesSection palette={palette} bundles={bundles} />
        <CommutesSection palette={palette} />
        
        {/* FINAL CTA: Guided Checklist Form */}
        <div className="mb-20">
          <GuidedFormSection
            id="quote"
            palette={palette}
            consultServices={consultServices}
            selectedServices={selectedServices}
            onSelectPrimary={handlePrimarySelect}
            onToggleService={handleServiceToggle}
            onFinish={() => {
              // Placeholder hook for analytics
              // console.log('Checklist request flow completed');
            }}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 border-t border-slate-200/60 bg-white/40 backdrop-blur-md">
        <HomeProsFooter palette={palette} />
      </div>

      {/* Sticky Mobile CTA */}
      <StickyQuoteButton palette={palette} />
      
    </div>
  );
}
